/**
 * Editing a dinner in place: what the sheet sends, what it says when the server
 * refuses, and what it offers to tell the guests afterwards.
 *
 * Why this exists: delete-and-recreate was the only way to correct a dinner, and
 * DELETE cancels every guest's claim. PATCH /dinner-holds/{id}
 * (OrchestratorAPI#395) edits the same hold, so the claims, the seating chart
 * binding and the hold id all survive. Everything here is pure — the sheet and
 * the tab wire it to the network, and nothing in this file sends anything.
 *
 * Three rules the wire imposes, each pinned by a test:
 *
 *  - PARTIAL means partial. A key that is absent is left alone; `null` clears.
 *    So the body carries ONLY what the host changed, never the whole form.
 *  - `timeZone` travels WITH `reservedAt`, always (the API 400s otherwise). The
 *    stored value is an instant and the zone only decides how it reads, so a
 *    zone sent alone silently moves the dinner's local time. Both are computed
 *    from the wall-clock time the host typed, in that zone (`wallClockToIso`,
 *    DST-safe), and both are sent whenever either changes.
 *  - The edit never releases a claim. A capacity below the claimed seats is
 *    refused twice: here, before the request, and by the server's 409.
 */
import { claimDisplayName } from './dinnerClaimEdit';
import { SMS_MAX, momentParts, smsDate, smsDraft, type ChangeNoticeDraft } from './changeNotice';
import { mutationErrorMessage } from './mutation-error';
import { isoToWallClock, resolveTripTimeZone, wallClockToIso } from './tripTime';
import type {
  DinnerSameEveningConflict,
  GroupDinnerHold,
  GroupInvitee,
  UpdateDinnerHoldRequest,
} from '../types/groups';

/** Upper bound the create form uses too: it keeps the value inside INT4. */
export const MAX_DINNER_SPOTS = 100000;

// ---------------------------------------------------------------------------
// The form and the diff
// ---------------------------------------------------------------------------

export interface DinnerEditForm {
  venueName: string;
  address: string;
  /** "YYYY-MM-DDTHH:mm" in the dinner's zone, '' for undated. */
  whenLocal: string;
  /** Kept as text so a half-typed number is not clobbered mid-keystroke. */
  totalSpots: string;
  notes: string;
}

export interface DinnerEditFormErrors {
  venueName?: string;
  totalSpots?: string;
  when?: string;
}

/**
 * The zone the sheet reads and writes the time in: the hold's own zone, else the
 * trip's. A hold zone that is not a real zone falls back to the trip's, not to
 * the browser's — the browser is wherever the host happens to be standing.
 */
export function dinnerEditZone(hold: Pick<GroupDinnerHold, 'timeZone'>, tripZone: string): string {
  const own = resolveTripTimeZone(hold.timeZone);
  return hold.timeZone && !own.isFallback ? own.timeZone : tripZone;
}

export function formFromHold(hold: GroupDinnerHold, zone: string): DinnerEditForm {
  return {
    venueName: hold.venueName || '',
    address: hold.address || '',
    whenLocal: isoToWallClock(hold.reservedAt, zone),
    totalSpots: String(hold.totalSpots),
    notes: hold.notes || '',
  };
}

function parseSpots(text: string): number {
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : Number.NaN;
}

/** Same bounds as the create form (and the API's non-negative INT4), minus zero. */
export function validateDinnerForm(form: DinnerEditForm, zone: string): DinnerEditFormErrors {
  const errors: DinnerEditFormErrors = {};
  if (!form.venueName.trim()) errors.venueName = 'A dinner needs a venue name.';
  const spots = parseSpots(form.totalSpots);
  if (!Number.isFinite(spots) || spots < 1 || spots > MAX_DINNER_SPOTS) {
    errors.totalSpots = `Spots must be a whole number from 1 to ${MAX_DINNER_SPOTS}.`;
  }
  if (form.whenLocal.trim() && !wallClockToIso(form.whenLocal, zone)) {
    errors.when = 'Enter a date and time, or clear the field to leave the dinner undated.';
  }
  return errors;
}

const squash = (text: string | null | undefined) => (text ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Whether the hold carries a map pin that a venue rename would leave pointing at
 * the old restaurant. The place is bound to the venue it was picked for: a wrong
 * pin is worse than none (no pin makes a guest ask; a wrong pin makes them
 * drive), so renaming to a different venue clears the pin, in one PATCH, and the
 * sheet says so. A capitalisation or spacing fix keeps it.
 */
export function venueRenameClearsPin(hold: GroupDinnerHold, form: Pick<DinnerEditForm, 'venueName'>): boolean {
  const hasPin = Boolean(hold.placeId) || hold.latitude != null || hold.longitude != null;
  return hasPin && squash(form.venueName) !== squash(hold.venueName);
}

export interface DinnerPatch {
  body: UpdateDinnerHoldRequest;
  /** The editable fields the body touches, for the sheet's own bookkeeping. */
  fields: string[];
}

/**
 * The request body: only what changed, or null when nothing did.
 *
 * `hold` is the hold as the host OPENED it, and `hold.sourceVersion` goes back as
 * `expectedSourceVersion` — a change made by someone else in the meantime is a
 * 409, not a silent overwrite. `zone` is the zone the form's wall-clock time is
 * in (`dinnerEditZone`).
 */
export function buildDinnerPatch(hold: GroupDinnerHold, form: DinnerEditForm, zone: string): DinnerPatch | null {
  const body: UpdateDinnerHoldRequest = {};
  const fields: string[] = [];

  const venueName = form.venueName.trim();
  if (venueName && venueName !== (hold.venueName || '').trim()) {
    body.venueName = venueName;
    fields.push('venueName');
    if (venueRenameClearsPin(hold, form)) {
      if (hold.placeId) body.placeId = null;
      if (hold.latitude != null || hold.longitude != null) {
        // A pair: the API 400s on one without the other.
        body.latitude = null;
        body.longitude = null;
      }
    }
  }

  const address = form.address.trim();
  const oldAddress = (hold.address || '').trim();
  if (address !== oldAddress) {
    body.address = address || null;
    fields.push('address');
  }

  const whenLocal = form.whenLocal.trim();
  if (whenLocal !== isoToWallClock(hold.reservedAt, zone)) {
    if (!whenLocal) {
      // Undated: the instant and its zone are cleared together.
      body.reservedAt = null;
      body.timeZone = null;
      fields.push('reservedAt');
    } else {
      const reservedAt = wallClockToIso(whenLocal, zone);
      if (reservedAt) {
        // Never one without the other — see the header.
        body.reservedAt = reservedAt;
        body.timeZone = zone;
        fields.push('reservedAt');
      }
    }
  }

  const spots = parseSpots(form.totalSpots);
  if (Number.isFinite(spots) && spots >= 1 && spots <= MAX_DINNER_SPOTS && spots !== hold.totalSpots) {
    body.totalSpots = spots;
    fields.push('totalSpots');
  }

  const notes = form.notes.trim();
  if (notes !== (hold.notes || '').trim()) {
    body.notes = notes || null;
    fields.push('notes');
  }

  if (fields.length === 0) return null;
  if (typeof hold.sourceVersion === 'number') body.expectedSourceVersion = hold.sourceVersion;
  return { body, fields };
}

/** True when a body puts a zone on the wire without the instant it belongs to. */
export function zoneWithoutInstant(body: UpdateDinnerHoldRequest): boolean {
  return 'timeZone' in body && !('reservedAt' in body);
}

// ---------------------------------------------------------------------------
// Capacity
// ---------------------------------------------------------------------------

export function capacityBelowClaimedMessage(claimedSeats: number): string {
  const seats = claimedSeats === 1 ? '1 seat is' : `${claimedSeats} seats are`;
  return `${seats} already claimed — capacity can't go below ${claimedSeats}. Release seats first if you need to.`;
}

export interface CapacityPrecheck {
  ok: boolean;
  message: string | null;
}

/**
 * Refuses, before the request, a capacity below the seats already claimed.
 *
 * Only when the capacity is being CHANGED: a table that is already over (a
 * shrink that raced a claim) must still let the host fix its time. The claimed
 * count is what the screen knows, so this is a courtesy that saves a round trip
 * — the server's `capacity_below_claimed` is still the authority, and nothing
 * here or there ever releases anybody to make room.
 */
export function capacityPrecheck(
  hold: Pick<GroupDinnerHold, 'totalSpots'>,
  claimedSeats: number,
  requestedSpots: number,
): CapacityPrecheck {
  if (!Number.isFinite(requestedSpots) || requestedSpots === hold.totalSpots) {
    return { ok: true, message: null };
  }
  if (requestedSpots < claimedSeats) {
    return { ok: false, message: capacityBelowClaimedMessage(claimedSeats) };
  }
  return { ok: true, message: null };
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

export type DinnerEditErrorKind = 'capacity_below_claimed' | 'hold_cancelled' | 'stale' | 'other';

export interface DinnerEditError {
  kind: DinnerEditErrorKind;
  message: string;
}

export const STALE_DINNER_MESSAGE = 'This dinner changed since you opened it — reload to see the latest.';

/** A 409's `code`, turned into the sentence and the branch the sheet needs. */
export function readDinnerEditError(error: unknown): DinnerEditError {
  const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  const data = response?.data;
  const code = response?.status === 409 && data && typeof data === 'object' ? data.code : undefined;

  if (code === 'capacity_below_claimed') {
    const claimed = Number(data?.claimedSeats);
    return {
      kind: 'capacity_below_claimed',
      message: Number.isFinite(claimed) && claimed >= 0
        ? capacityBelowClaimedMessage(claimed)
        : "Capacity can't go below the seats already claimed. Release seats first if you need to.",
    };
  }
  if (code === 'hold_cancelled') {
    return { kind: 'hold_cancelled', message: "This dinner was cancelled, so it can't be edited." };
  }
  if (code === 'stale') return { kind: 'stale', message: STALE_DINNER_MESSAGE };
  return {
    kind: 'other',
    message: mutationErrorMessage(error) || 'Could not save this dinner. Check your connection and try again.',
  };
}

// ---------------------------------------------------------------------------
// After a save
// ---------------------------------------------------------------------------

/**
 * "Sam now has two dinners that evening (also at Belcanto)." — one sentence per
 * guest, from the server's `sameEveningConflicts`. A warning, never a block: the
 * edit already happened and no claim was touched, so the sentence says who has a
 * decision to make, not that something went wrong.
 */
export function conflictNote(
  conflicts: readonly DinnerSameEveningConflict[],
  hold: Pick<GroupDinnerHold, 'claims'>,
  roster: readonly GroupInvitee[],
): string | null {
  if (conflicts.length === 0) return null;
  const byGuest = new Map<string, string[]>();
  for (const conflict of conflicts) {
    const venues = byGuest.get(conflict.inviteeId) ?? [];
    if (conflict.otherVenueName && !venues.includes(conflict.otherVenueName)) venues.push(conflict.otherVenueName);
    byGuest.set(conflict.inviteeId, venues);
  }
  const sentences: string[] = [];
  byGuest.forEach((venues, inviteeId) => {
    const claim = hold.claims.find((candidate) => candidate.inviteeId === inviteeId);
    const name = claimDisplayName(claim ?? { inviteeId, guestName: '' }, roster);
    const also = venues.length > 0 ? ` (also at ${venues.join(' and ')})` : '';
    sentences.push(`${name} now has two dinners that evening${also}.`);
  });
  return `${sentences.join(' ')} Nobody was released — decide which dinner each of them keeps.`;
}

/** Fields whose change a guest would notice. Capacity and zone-alone are not. */
const GUEST_MEANINGFUL = ['reservedAt', 'timeZone', 'venueName', 'placeId', 'address', 'notes'];

export function hasGuestMeaningfulChange(changedFields: readonly string[] | null | undefined): boolean {
  return (changedFields ?? []).some((field) => GUEST_MEANINGFUL.includes(field));
}

export function unlinkedSeatsLine(unlinkedClaimSeats: number): string | null {
  if (!(unlinkedClaimSeats > 0)) return null;
  const seats = unlinkedClaimSeats === 1 ? '1 seat was' : `${unlinkedClaimSeats} seats were`;
  return `${seats} added by hand and can't be messaged — tell them directly.`;
}

export interface DinnerNoticeAudience {
  /** Explicit invitee ids — the claimants and nobody else. Never empty. */
  inviteeIds: string[];
  /** "the 6 guests with seats at this dinner", for the dialog to say out loud. */
  label: string;
  /** Set when some seats cannot be reached by invitee id. */
  unlinkedNote: string | null;
}

export interface DinnerChangeOffer {
  notice: ChangeNoticeDraft;
  audience: DinnerNoticeAudience;
}

export type DinnerChangeKind = 'time' | 'venue' | 'time_and_venue' | 'address' | 'notes' | 'multiple';

/** Braces are variables to the broadcast API ({name}, {url}); a venue must not be one. */
const plain = (text: string | null | undefined) => (text ?? '').replace(/[{}]/g, '').trim();

function zoneFor(holdZone: string | null | undefined, tripZone: string): string {
  const own = resolveTripTimeZone(holdZone);
  return holdZone && !own.isFallback ? own.timeZone : tripZone;
}

interface When {
  email: string;
  sms: string;
  date: string;
  time: string;
}

function whenOf(iso: string | null | undefined, zone: string): When | null {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return null;
  const parts = momentParts(iso, zone);
  return {
    email: `${parts.date}, ${parts.time}`,
    sms: `${smsDate(parts)}, ${parts.time}`,
    date: smsDate(parts),
    time: parts.time,
  };
}

/**
 * The change notice for a saved dinner edit, offered to the claimants ONLY — or
 * null when there is nothing to offer.
 *
 * Null when no guest holds a seat (nobody to tell, and an empty id list would
 * read as "everyone" to the broadcast API), and when nothing a guest would
 * notice changed: capacity and the map pin are the host's business.
 *
 * `before` is the hold as the host opened the sheet and `after` is what the
 * server echoed. What changed comes from the server's `changedFields`, so a
 * no-op edit or an offset-only change offers nothing. The copy is distinct per
 * kind of change, names the dinner and reads the new time in the dinner's zone,
 * and the SMS is plain GSM-7 under 280 characters with no link — the concierge
 * appends the trip link itself.
 */
export function buildDinnerChangeOffer(input: {
  before: GroupDinnerHold;
  after: GroupDinnerHold;
  tripZone: string;
  changedFields: readonly string[] | null | undefined;
  claimantInviteeIds: readonly string[] | null | undefined;
  unlinkedClaimSeats?: number | null;
}): DinnerChangeOffer | null {
  const { before, after, tripZone } = input;
  const changed = new Set(input.changedFields ?? []);
  const inviteeIds = Array.from(new Set((input.claimantInviteeIds ?? []).filter(Boolean)));
  if (inviteeIds.length === 0 || !hasGuestMeaningfulChange(input.changedFields)) return null;

  const beforeWhen = whenOf(before.reservedAt, zoneFor(before.timeZone, tripZone));
  const afterZone = zoneFor(after.timeZone, tripZone);
  const afterWhen = whenOf(after.reservedAt, afterZone);
  const timeChanged = (changed.has('reservedAt') || changed.has('timeZone'))
    && (beforeWhen?.email ?? '') !== (afterWhen?.email ?? '');
  const oldVenue = plain(before.venueName) || 'the dinner';
  const newVenue = plain(after.venueName) || oldVenue;
  const venueChanged = changed.has('venueName') && oldVenue !== newVenue;
  const oldAddress = plain(before.address);
  const newAddress = plain(after.address);
  const addressChanged = changed.has('address') && oldAddress !== newAddress;
  const notesChanged = changed.has('notes') && plain(before.notes) !== plain(after.notes);

  const kinds: DinnerChangeKind[] = [];
  if (timeChanged) kinds.push('time');
  if (venueChanged) kinds.push('venue');
  if (addressChanged) kinds.push('address');
  if (notesChanged) kinds.push('notes');
  if (kinds.length === 0) return null;
  const kind: DinnerChangeKind = kinds.length === 1
    ? kinds[0]
    : kinds.length === 2 && timeChanged && venueChanged ? 'time_and_venue' : 'multiple';

  // One line per changed field, spelled once for the email and once for the SMS.
  const emailLines: string[] = [];
  const smsFragments: string[] = [];
  if (timeChanged) {
    if (beforeWhen && afterWhen) {
      emailLines.push(`Time: ${beforeWhen.email} → ${afterWhen.email} (${afterZone})`);
      smsFragments.push(beforeWhen.date === afterWhen.date
        ? `${afterWhen.date}, now ${afterWhen.time} (was ${beforeWhen.time})`
        : `now ${afterWhen.sms} (was ${beforeWhen.sms})`);
    } else if (afterWhen) {
      emailLines.push(`Time: ${afterWhen.email} (${afterZone})`);
      smsFragments.push(`now set for ${afterWhen.sms}`);
    } else {
      emailLines.push(`Time: to be confirmed (was ${beforeWhen?.email ?? 'not set'})`);
      smsFragments.push('time to be confirmed');
    }
  }
  if (venueChanged) {
    emailLines.push(`Venue: ${oldVenue} → ${newVenue}`);
    smsFragments.push(`now at ${newVenue} (was ${oldVenue})`);
  }
  if (addressChanged) {
    emailLines.push(newAddress ? `Address: ${newAddress}` : 'Address: removed');
    smsFragments.push(newAddress ? `new address ${newAddress}` : 'address removed');
  }
  if (notesChanged) {
    const notes = plain(after.notes);
    emailLines.push(notes ? `Notes from the host: ${notes}` : 'The host removed the notes.');
    smsFragments.push('the host updated the notes');
  }

  let headline: string;
  let subject: string;
  let sms: string;
  switch (kind) {
    case 'time':
      headline = `Dinner at ${newVenue} moved`;
      subject = `Dinner at ${newVenue} moved`;
      sms = `Dinner at ${newVenue} moved: ${smsFragments[0]}`;
      break;
    case 'venue':
      headline = 'The dinner venue changed';
      subject = 'Dinner venue changed';
      sms = `Dinner venue changed: ${smsFragments[0]}${afterWhen ? `. Still ${afterWhen.sms}` : ''}`;
      break;
    case 'time_and_venue':
      headline = 'The dinner moved';
      subject = 'Dinner moved';
      sms = afterWhen && beforeWhen
        ? `Dinner moved: now at ${newVenue}, ${afterWhen.sms} (was ${oldVenue}, ${beforeWhen.sms})`
        : `Dinner moved: ${smsFragments.join('; ')}`;
      break;
    case 'address':
      headline = `Dinner at ${newVenue}: new address`;
      subject = `Dinner at ${newVenue}: new address`;
      sms = `Dinner at ${newVenue}: ${smsFragments[0]}`;
      break;
    case 'notes':
      headline = `Dinner at ${newVenue}: updated details`;
      subject = `Dinner at ${newVenue}: updated details`;
      sms = `Dinner at ${newVenue}${afterWhen ? ` (${afterWhen.sms})` : ''}: the host updated the notes`;
      break;
    default:
      headline = `Dinner at ${newVenue} changed`;
      subject = `Change to dinner at ${newVenue}`;
      sms = `Dinner at ${newVenue} changed: ${smsFragments.join('; ')}`;
  }

  const draft = smsDraft(`${sms}.`);
  const notice: ChangeNoticeDraft = {
    summary: `${headline}: ${emailLines.join('; ')}`,
    subject,
    emailBody: `${headline}.\n\n${emailLines.join('\n')}\n\nSee the details here: {url}`,
    smsText: draft.smsText.length <= SMS_MAX ? draft.smsText : draft.smsText.slice(0, SMS_MAX),
    smsWarning: draft.smsWarning,
  };

  const count = inviteeIds.length;
  return {
    notice,
    audience: {
      inviteeIds,
      label: count === 1
        ? 'the 1 guest with a seat at this dinner'
        : `the ${count} guests with seats at this dinner`,
      unlinkedNote: unlinkedSeatsLine(input.unlinkedClaimSeats ?? 0),
    },
  };
}
